import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * Cancellation & Auto-Renewal Disclosure — APPROVED FINAL (May 26, 2026).
 *
 * Designed to satisfy California's Automatic Renewal Law (Bus. & Prof.
 * Code §17600 et seq.), the federal Restore Online Shoppers'
 * Confidence Act (ROSCA), and analogous state laws.
 *
 * The actual 7-business-day pre-charge reminder email is implemented in
 * `backend/services/automation_renewal_notices.py` and registered as
 * the `auto-renewal-notices` automation in `routers/automations.py`.
 *
 * Section numbering follows the published copy — do not renumber
 * without legal review.
 */
export default function AutoRenewDisclosure() {
  return (
    <LegalLayout
      slug="auto-renew"
      eyebrow="Subscriptions"
      title="Subscription Cancellation &amp; Auto-Renewal Disclosure"
      lastUpdated="May 26, 2026"
    >
      <p>
        This Cancellation &amp; Auto-Renewal Policy outlines your rights
        regarding subscription services offered by Armstrong K9 Registry. It
        is designed to ensure transparency and compliance with applicable
        laws, empowering you with important information about your
        subscription.
      </p>

      <h2>1. What subscriptions we offer</h2>
      <p>
        Armstrong K9 Registry sells the following recurring subscriptions.
        Exact prices may differ from the amounts shown if your kennel is on a
        grandfathered tier or a promotional rate.
      </p>
      <ul>
        <li>
          <strong>Buyer Pass</strong> &mdash; verified-buyer status. Monthly
          or annual term. Auto-renews unless cancelled.
        </li>
        <li>
          <strong>Storefront Subscription</strong> &mdash; public kennel
          storefront slot under <code>/store/&lt;slug&gt;</code>. Monthly
          term. Auto-renews unless cancelled.
        </li>
        <li>
          <strong>Dog Log Premium</strong> &mdash; full care-tracking surface
          (10 categories + sitter share + symptom warnings). Monthly or
          annual term. Auto-renews unless cancelled.
        </li>
        <li>
          <strong>Featured Kennel</strong> &mdash; homepage rotation slot.
          Monthly term. Auto-renews unless cancelled.
        </li>
      </ul>

      <h2>2. How auto-renewal works</h2>
      <ul>
        <li>
          Your subscription <strong>automatically renews</strong> at the end
          of each billing period at the then-current price for the same plan
          length, charged to the payment method on file via Stripe.
        </li>
        <li>
          <strong>Advance renewal reminder.</strong> We email you a renewal
          reminder to your account email at least <strong>7 business days</strong>{" "}
          before each charge, disclosing the amount, date, payment-method
          last-4, and a one-click cancel link to{" "}
          <Link to="/billing">Billing</Link>.
        </li>
        <li>
          Renewal continues until you cancel through the in-app{" "}
          <Link to="/billing">Billing</Link> page or by emailing us at the
          address below.
        </li>
        <li>
          If a renewal fails (expired card, insufficient funds), we retry
          once after 48 hours and then suspend the subscription. We will
          email you on the first retry and on suspension.
        </li>
      </ul>

      <h2>3. How to cancel your subscription</h2>
      <p>
        You can cancel at any time, for any reason, with no penalty. The
        cancellation method is the same one used for sign-up:
      </p>
      <ul>
        <li>
          <strong>In-app:</strong>
          <ol>
            <li>Open the <Link to="/billing">Billing</Link> page in the app.</li>
            <li>Select your active subscription.</li>
            <li>
              Click &ldquo;Cancel auto-renewal.&rdquo; Cancellation is
              effective at the end of the current billing period, allowing
              continued access until that time.
            </li>
          </ol>
        </li>
        <li>
          <strong>By email:</strong> Send &ldquo;Cancel subscription&rdquo;
          to{" "}
          <a href="mailto:billing@armstrongk9registry.com">
            billing@armstrongk9registry.com
          </a>{" "}
          from the email address on the account.
        </li>
      </ul>

      <h2>4. Refunds</h2>
      <p>
        Subscriptions are billed in advance and are{" "}
        <strong>non-refundable</strong> once the billing period has started.
        If you cancel mid-period, you keep access through the end of that
        period &mdash; no refund of the unused remainder is issued.
      </p>
      <p>
        <strong>Exception:</strong> if you cancel within <strong>3 days</strong>{" "}
        of an auto-renewal AND you have not used a paid feature inside that
        new period, we issue a full refund on request via the email above.
      </p>
      <p>
        One-time fees (dog registration, pedigree certificate, video tour)
        follow the <Link to="/refund">Refund Policy</Link> &mdash; not this
        disclosure.
      </p>

      <h2>5. Price changes</h2>
      <p>
        If we raise the price of your subscription, we notify you at least{" "}
        <strong>30 days</strong> before the next renewal at the new amount.
        You can cancel before the renewal at no charge if you do not accept
        the new price.
      </p>

      <h2>6. Free trials / promotional pricing</h2>
      <p>
        Some subscriptions may be offered with a free trial or promotional
        first-period rate. At the end of the trial or promo period, your
        plan will auto-renew at the then-current standard price. We notify
        you by email at least 3 days before the promo period ends so you can
        cancel if you choose.
      </p>

      <h2>7. Where this disclosure applies</h2>
      <p>
        These terms apply globally to all paying subscribers. California
        Bus. &amp; Prof. Code §17602(b) requires us to display this notice
        in a &ldquo;clear and conspicuous&rdquo; manner before charging, and
        to provide an easy online cancellation method &mdash; both of which
        are satisfied here and in the in-app{" "}
        <Link to="/billing">Billing</Link> surface.
      </p>

      <h2>8. Questions</h2>
      <p>
        Billing questions:{" "}
        <a href="mailto:billing@armstrongk9registry.com">
          billing@armstrongk9registry.com
        </a>
        . We respond within 2 business days.
      </p>
    </LegalLayout>
  );
}
